import { Fragment, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useRouter } from "@tanstack/react-router";
import { Bug, Check, Lightbulb, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSession } from "@/hooks/use-auth";
import {
  useBugReportThread,
  useSendBugReportMessage,
  useSetBugReportStatus,
  type BugReportMessage,
} from "@/hooks/use-bug-reports";
import { canManageBugReports } from "@/lib/roles";
import { relativeTime } from "@/lib/relative-time";
import { cn, formatDateTime } from "@/lib/utils";
import { AttachmentStrip, AttachmentViewer } from "./attachments";
import { Description } from "./description";

const MESSAGE_MAX = 10_000;

const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
const SEND_KEYS = IS_MAC ? "⌘↵" : "Ctrl+Enter";

const URL_PATTERN = /(https?:\/\/[^\s<>"]+)/g;

/** Dev često odgovara s linkom („pogledaj ovdje“) — neka bude klikabilan. */
function Linkified({ text }: { text: string }) {
  const parts = text.split(URL_PATTERN);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noreferrer"
            className="break-all underline underline-offset-2 opacity-90 hover:opacity-100"
          >
            {part}
          </a>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  );
}

function MessageBubble({ message, own }: { message: BugReportMessage; own: boolean }) {
  const date = new Date(message.createdAt * 1000);
  return (
    <li className={cn("flex flex-col gap-1", own ? "items-end" : "items-start")}>
      <div
        className={cn(
          "max-w-[85%] whitespace-pre-wrap break-words px-3 py-2 text-[13px] leading-relaxed",
          own ? "bg-foreground text-background" : "border bg-muted/50",
        )}
      >
        <Linkified text={message.body} />
      </div>
      <p className="text-[11px] text-muted-foreground">
        {own ? "Ti" : message.authorName || "Nepoznat"}
        <span aria-hidden> · </span>
        <time dateTime={date.toISOString()} title={formatDateTime(date)}>
          {relativeTime(message.createdAt)}
        </time>
      </p>
    </li>
  );
}

/**
 * Tiket prijave s razgovorom — otvara se i iz liste prijava (menadžer) i iz zvonca
 * (prijavilac ili dev). Pristup provjerava server; ovdje se samo prikazuje greška.
 */
export function TicketDialog({
  reportId,
  onClose,
}: {
  reportId: string | null;
  onClose: () => void;
}) {
  // Zadnji otvoreni tiket ostaje na ekranu dok se dijalog animirano zatvara.
  const [shownId, setShownId] = useState(reportId);
  if (reportId && reportId !== shownId) setShownId(reportId);

  const router = useRouter();
  const { user } = useSession();
  const isManager = canManageBugReports(user);
  const { data: thread, isLoading, error } = useBugReportThread(shownId);
  const send = useSendBugReportMessage();
  const setStatus = useSetBugReportStatus();

  const [draft, setDraft] = useState("");
  const [viewing, setViewing] = useState<number | null>(null);
  // Koje dugme je pokrenulo slanje — spinner ide samo na njega.
  const [busy, setBusy] = useState<"send" | "resolve" | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const seenCount = useRef<number | null>(null);

  // Nacrt pripada jednom tiketu; drugi tiket počinje praznim poljem.
  useEffect(() => {
    setDraft("");
    setViewing(null);
    seenCount.current = null;
  }, [shownId]);

  // Pri otvaranju ostaje vrh (opis daje kontekst); nova poruka spušta razgovor do dna.
  const messageCount = thread?.messages.length ?? 0;
  useEffect(() => {
    if (!thread) return;
    if (seenCount.current !== null && messageCount > seenCount.current) {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
    seenCount.current = messageCount;
  }, [thread, messageCount]);

  const pending = busy !== null;
  const resolved = thread?.status === "rijesen";
  const isBug = thread?.type === "bug";
  const TypeIcon = isBug ? Bug : Lightbulb;

  async function handleSend(resolve: boolean) {
    const body = draft.trim();
    if (!thread || !body || pending) return;
    setBusy(resolve ? "resolve" : "send");
    try {
      await send.mutateAsync({ id: thread.id, body });
      setDraft("");
      if (resolve) {
        try {
          await setStatus.mutateAsync({ id: thread.id, status: "rijesen" });
          toast.success("Odgovor poslan, prijava riješena");
        } catch (e: any) {
          toast.error(e?.message || "Poruka je poslana, ali prijava nije označena kao riješena");
        }
      }
    } catch (e: any) {
      toast.error(e?.message || "Greška pri slanju poruke");
    } finally {
      setBusy(null);
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void handleSend(false);
    }
  }

  return (
    <Dialog open={reportId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[92vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
        {!thread ? (
          <>
            <DialogHeader className="sr-only">
              <DialogTitle>Prijava</DialogTitle>
              <DialogDescription>Učitavanje prijave</DialogDescription>
            </DialogHeader>
            {isLoading || !error ? (
              <div className="space-y-3 px-6 py-5" aria-busy="true">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-6 w-3/5" />
                <Skeleton className="h-3 w-2/5" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : (
              <p role="alert" className="px-6 py-10 text-center text-sm text-muted-foreground">
                {error.message || "Prijava se ne može otvoriti."}
              </p>
            )}
          </>
        ) : (
          <>
            <DialogHeader className="gap-2 border-b px-6 py-5 pr-12 text-left">
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge
                  variant="outline"
                  className={cn(
                    "h-5 px-1.5 text-[10px] uppercase tracking-wide",
                    isBug && !resolved ? "text-destructive" : "text-foreground/70",
                  )}
                >
                  <TypeIcon />
                  {isBug ? "Bug" : "Prijedlog"}
                </Badge>
                <Badge
                  variant={resolved ? "default" : "secondary"}
                  className="h-5 px-1.5 text-[10px] uppercase tracking-wide"
                >
                  {resolved && <Check />}
                  {resolved ? "Riješena" : "Otvorena"}
                </Badge>
              </div>
              <DialogTitle className="break-words text-lg font-semibold leading-snug tracking-tight">
                {thread.title}
              </DialogTitle>
              <DialogDescription className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px]">
                <span>Prijavio {thread.reportedBy || "Nepoznat"}</span>
                <span aria-hidden>·</span>
                <time dateTime={new Date(thread.createdAt * 1000).toISOString()} className="tabular-nums">
                  {formatDateTime(new Date(thread.createdAt * 1000))}
                </time>
                {thread.pageUrl && (
                  <>
                    <span aria-hidden>·</span>
                    <a
                      href={thread.pageUrl}
                      title="Otvori stranicu s koje je prijava poslana"
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                        e.preventDefault();
                        onClose();
                        router.history.push(thread.pageUrl);
                      }}
                      className="max-w-full truncate underline decoration-foreground/30 underline-offset-2 outline-none hover:text-foreground hover:decoration-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    >
                      {thread.pageUrl}
                    </a>
                  </>
                )}
              </DialogDescription>
            </DialogHeader>

            <div ref={scrollRef} className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
              {thread.description ? (
                <Description text={thread.description} collapsible={false} />
              ) : (
                <p className="text-[13px] text-muted-foreground">Bez opisa.</p>
              )}

              <AttachmentStrip report={thread} onOpen={setViewing} />

              {resolved && thread.resolvedAt && (
                <p className="text-[11px] text-muted-foreground">
                  Riješio {thread.resolvedBy || "—"} ·{" "}
                  {formatDateTime(new Date(thread.resolvedAt * 1000))}
                </p>
              )}

              <section className="space-y-3">
                <h3 className="border-b pb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Razgovor
                </h3>
                {thread.messages.length === 0 ? (
                  <p className="text-[13px] text-muted-foreground">
                    Još nema poruka.
                    {isManager ? " Javi prijaviocu šta je urađeno." : " Ovdje stižu odgovori na prijavu."}
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {thread.messages.map((m) => (
                      <MessageBubble key={m.id} message={m} own={!!user && m.authorId === user.id} />
                    ))}
                  </ul>
                )}
              </section>
            </div>

            <div className="space-y-3 border-t bg-muted/40 px-6 py-4">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Napiši odgovor…"
                aria-label="Odgovor"
                maxLength={MESSAGE_MAX}
                disabled={pending}
                className="max-h-48 min-h-20 rounded-none bg-background px-3 py-2.5 text-sm leading-relaxed shadow-none dark:bg-background"
              />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-[11px] text-muted-foreground">{SEND_KEYS} šalje</span>
                <div className="flex gap-2">
                  {isManager && !resolved && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pending || !draft.trim()}
                      onClick={() => void handleSend(true)}
                    >
                      {busy === "resolve" ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Check className="size-4" />
                      )}
                      Pošalji i riješi
                    </Button>
                  )}
                  <Button size="sm" disabled={pending || !draft.trim()} onClick={() => void handleSend(false)}>
                    {busy === "send" && <Loader2 className="size-4 animate-spin" />}
                    Pošalji
                  </Button>
                </div>
              </div>
            </div>

            {viewing !== null && (
              <AttachmentViewer
                report={thread}
                index={viewing}
                onIndexChange={setViewing}
                onClose={() => setViewing(null)}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
